/**
 * Satoshi/millisatoshi conversion utilities for NWC protocol boundaries.
 *
 * NWC (NIP-47) uses millisatoshis for all amounts, while the app uses
 * satoshis internally. These functions handle conversion at every NWC
 * method call and response boundary.
 *
 * Key design decision: msatsToSats always floors (never rounds up) to
 * never overstate a balance or payment amount. Losing a fraction of a
 * millisatoshi is safer than displaying more than the user actually has.
 *
 * Tier: All tiers (core infrastructure).
 *
 * @module lib/amount-conversion
 */

/** Maximum safe satoshi value (Number.MAX_SAFE_INTEGER / 1000, floored). */
const MAX_SAFE_SATS = Math.floor(Number.MAX_SAFE_INTEGER / 1000);

/**
 * Convert satoshis to millisatoshis.
 *
 * @param sats - Amount in satoshis (must be non-negative integer)
 * @returns Amount in millisatoshis
 * @throws {RangeError} If sats is negative, non-integer, or would overflow
 */
export function satsToMsats(sats: number): number {
  if (sats < 0) {
    throw new RangeError('Satoshi amount must be non-negative');
  }
  if (!Number.isInteger(sats)) {
    throw new RangeError('Satoshi amount must be an integer');
  }
  if (sats > MAX_SAFE_SATS) {
    throw new RangeError(
      `Satoshi amount exceeds safe conversion limit (${MAX_SAFE_SATS.toString()})`,
    );
  }
  return sats * 1000;
}

/**
 * Convert millisatoshis to satoshis, always rounding down.
 *
 * Floors the result to never overstate a balance. A fractional
 * millisatoshi is lost rather than displayed as a full satoshi.
 *
 * @param msats - Amount in millisatoshis (must be non-negative integer)
 * @returns Amount in satoshis (floored)
 * @throws {RangeError} If msats is negative or non-integer
 */
export function msatsToSats(msats: number): number {
  if (msats < 0) {
    throw new RangeError('Millisatoshi amount must be non-negative');
  }
  if (!Number.isInteger(msats)) {
    throw new RangeError('Millisatoshi amount must be an integer');
  }
  return Math.floor(msats / 1000);
}
